import { useParams, Link } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Sparkles, 
  Home, 
  Sun, 
  Car,
  Phone, 
  CheckCircle2, 
  ArrowRight,
  ArrowLeft,
  Droplets,
  Shield,
  Clock,
  Star,
  Zap,
  ChevronDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import PriceCalculator from "@/components/PriceCalculator";
import PreFooterBanner from "@/components/PreFooterBanner";
import MobileBottomNav from "@/components/MobileBottomNav";
import TestimonialsSection from "@/components/TestimonialsSection";
import { WindowIcon, RoofIcon, PaversIcon, SolarIcon } from "@/components/ServiceIcons";
import { SEO_CONFIG, updateMetaTags, injectServiceSchema } from "@/lib/seo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Google Reviews Section for Service Pages
const GoogleReviewsSection = () => {
  return (
    <section className="py-16 bg-gradient-to-b from-slate-50 to-white" data-testid="google-reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-slate-100 max-w-4xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-6">
              {/* Google Logo */}
              <div className="flex-shrink-0">
                <svg className="w-12 h-12" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-4xl font-bold text-slate-900" style={{ fontFamily: 'Instrument Sans, sans-serif' }}>4.8</span>
                  <div className="flex items-center">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star} 
                        className="w-6 h-6 text-yellow-400 fill-yellow-400"
                      />
                    ))}
                  </div>
                </div>
                <p className="text-slate-600">Google Reviews</p>
              </div>
            </div>
            <a
              href="https://www.google.com/search?gs_ssp=eJzj4tVP1zc0zEmxKIxPsqgyYLRSNagwMU82NDNMSTYzTzQzMTBIsjKoSLVMMjJNTbIwSTFLNTK3SPTiSitKLc5QKMstMLQcAa-gTTg&q=fresh+view&rlz=1CDGOYI_enBE1051BE1051&oq=f&gs_lcrp=EgZjaHJvbWUqFQgBEC4YQxivARjHARi6AhiABBiKBTIGCAAQRRg5MhUIARAuGEMYrwEYxwEYugIYgAQYigUyBggCECMYJzIQCAMQLhivARjHARi6AhiABDIHCAQQABiABDITCAUQLhiDARjHARixAxjRAxiABDISCAYQLhhDGMcBGNEDGIAEGIoFMgoIBxAAGLEDGIAEMgwICBAuGEMYgAQYigXSAQgxNTg3ajBqN6gCGrACAeIDBBgBIF_xBZHP_QFHKTbD8QWRz_0BRyk2ww&hl=nl&sourceid=chrome-mobile&ie=UTF-8#mpd=~10832033236040117453/customers/reviews"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full px-8 py-4 bg-sky-500 hover:bg-sky-600 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              data-testid="google-reviews-link"
            >
              Bekijk Reviews
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Image Slider Component for Before/After photos
const ImageSlider = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="relative max-w-3xl mx-auto">
      <div className="overflow-hidden rounded-2xl shadow-xl">
        <img 
          src={images[currentIndex]} 
          alt={`Voor en na ${currentIndex + 1}`}
          className="w-full h-[400px] md:h-[500px] object-cover transition-opacity duration-300"
        />
      </div>
      
      {/* Navigation Arrows */}
      <button 
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
        data-testid="slider-prev"
      >
        <ArrowLeft className="w-6 h-6 text-slate-700" />
      </button>
      <button 
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
        data-testid="slider-next"
      >
        <ArrowRight className="w-6 h-6 text-slate-700" />
      </button>
      
      {/* Dots Indicator */}
      <div className="flex justify-center gap-2 mt-6">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === currentIndex 
                ? 'bg-sky-500 w-8' 
                : 'bg-slate-300 hover:bg-slate-400'
            }`}
            data-testid={`slider-dot-${index}`}
          />
        ))}
      </div>
    </div>
  );
};

// Before/After Slider Component (drag to reveal)
const BeforeAfterSlider = ({ before, after, aspect = "aspect-[4/5] sm:aspect-[16/10]" }) => {
  const containerRef = useRef(null);
  const beforeImgRef = useRef(null);
  const handleRef = useRef(null);
  const hintRef = useRef(null);
  const isDragging = useRef(false);
  const rafId = useRef(null);
  const lastPct = useRef(50);
  const hasInteracted = useRef(false);
  const touchStart = useRef({ x: 0, y: 0, decided: null });

  // Directly mutate DOM (no React re-render) for buttery 60fps drag.
  const applyPct = (pct) => {
    lastPct.current = pct;
    if (beforeImgRef.current) {
      beforeImgRef.current.style.clipPath = `inset(0 ${100 - pct}% 0 0)`;
    }
    if (handleRef.current) {
      handleRef.current.style.left = `${pct}%`;
    }
  };

  const scheduleMove = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
    if (rafId.current) return; // coalesce with next frame
    rafId.current = requestAnimationFrame(() => {
      rafId.current = null;
      applyPct(pct);
    });
  };

  const hideHint = () => {
    if (hasInteracted.current) return;
    hasInteracted.current = true;
    if (hintRef.current) {
      hintRef.current.style.opacity = '0';
    }
  };

  const startDrag = (clientX) => {
    isDragging.current = true;
    hideHint();
    scheduleMove(clientX);
  };
  const stopDrag = () => { isDragging.current = false; };

  const onMouseDown = (e) => startDrag(e.clientX);
  const onMouseMove = (e) => { if (isDragging.current) scheduleMove(e.clientX); };

  // Direction-aware native touch listeners:
  // - Horizontal drag  -> slider moves, page scroll blocked (preventDefault)
  // - Vertical drag    -> let the browser scroll normally, ignore drag
  // Combined with CSS `touch-action: pan-y` this also keeps trackpad
  // scrolling working on desktops/laptops.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const THRESHOLD = 6; // px before deciding gesture direction

    const onTouchStart = (e) => {
      if (!e.touches[0]) return;
      touchStart.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        decided: null,
      };
      // Don't start drag or preventDefault yet - wait to see the direction
    };

    const onTouchMove = (e) => {
      if (!e.touches[0]) return;
      const dx = e.touches[0].clientX - touchStart.current.x;
      const dy = e.touches[0].clientY - touchStart.current.y;

      if (touchStart.current.decided === null) {
        if (Math.abs(dx) < THRESHOLD && Math.abs(dy) < THRESHOLD) return;
        touchStart.current.decided =
          Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
        if (touchStart.current.decided === 'horizontal') {
          startDrag(e.touches[0].clientX);
        }
      }

      if (touchStart.current.decided === 'horizontal') {
        scheduleMove(e.touches[0].clientX);
        e.preventDefault();
      }
      // vertical -> let browser scroll, do nothing
    };

    const onTouchEnd = () => {
      stopDrag();
      touchStart.current.decided = null;
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchcancel', onTouchEnd);
    // Initialize at 50%
    applyPct(50);
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={containerRef}
      className={`relative w-full ${aspect} rounded-2xl overflow-hidden cursor-ew-resize select-none shadow-xl bg-slate-100`}
      style={{ touchAction: 'pan-y', willChange: 'contents' }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={stopDrag}
      onMouseLeave={stopDrag}
      data-testid="before-after-slider"
    >
      {/* After Image (Background - visible on the right) */}
      <img
        src={after}
        alt="Na reiniging"
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        draggable={false}
      />

      {/* Before Image (Clipped via clip-path - visible on the left) */}
      <img
        ref={beforeImgRef}
        src={before}
        alt="Voor reiniging"
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        draggable={false}
        style={{ clipPath: 'inset(0 50% 0 0)', willChange: 'clip-path' }}
      />

      {/* Slider Handle */}
      <div
        ref={handleRef}
        className="absolute top-0 bottom-0 w-0.5 bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.15)] pointer-events-none"
        style={{ left: '50%', transform: 'translateX(-50%)', willChange: 'left' }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-xl flex items-center justify-center">
          <ArrowLeft className="w-3.5 h-3.5 text-slate-700 -mr-1" />
          <ArrowRight className="w-3.5 h-3.5 text-slate-700 -ml-1" />
        </div>
      </div>

      {/* Labels */}
      <div className="absolute top-2.5 left-2.5 px-2 py-0.5 bg-slate-900/80 backdrop-blur text-white text-[10px] sm:text-xs font-semibold rounded-full tracking-wider">
        VOOR
      </div>
      <div className="absolute top-2.5 right-2.5 px-2 py-0.5 bg-emerald-500 text-white text-[10px] sm:text-xs font-semibold rounded-full tracking-wider">
        NA
      </div>

      {/* Hint (fades on first interaction) */}
      <div
        ref={hintRef}
        className="absolute bottom-3 left-1/2 -translate-x-1/2 px-2.5 py-0.5 bg-white/90 backdrop-blur text-slate-700 text-[10px] sm:text-xs font-medium rounded-full shadow pointer-events-none transition-opacity duration-300"
      >
        Sleep om te vergelijken
      </div>
    </div>
  );
};

// FAQ Item Component with Accordion
const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
      >
        <span className="font-semibold text-slate-900 pr-4 text-sm sm:text-base leading-snug">{question}</span>
        <ChevronDown 
          className={`w-5 h-5 text-slate-500 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      <div 
        className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96' : 'max-h-0'}`}
      >
        <div className="px-4 sm:px-6 py-3 sm:py-4 bg-slate-50 border-t border-slate-200">
          <p className="text-slate-600 text-sm sm:text-base leading-relaxed">{answer}</p>
        </div>
      </div>
    </div>
  );
};

// Service data
const serviceData = {
  "ramen-wassen": {
    id: "ramen-wassen",
    title: "Ramen wassen",
    subtitle: "Ramen wassen. Streeploos resultaat met osmosewater. Ook ramen die moeilijk bereikbaar zijn.",
    description: "We reinigen ramen aan de binnen- en buitenkant. Met osmosewater dat vlekkeloos opdroogt.",
    icon: WindowIcon,
    heroImage: "/images/ramen.webp",
    heroObjectPositionClass: "object-[25%_center] sm:object-[center_center]",
    startingPrice: "€100",
    services: [
      {
        title: "Buitenzijde",
        desc: "Alle ramen streeploos gereinigd met osmosewater."
      },
      {
        title: "Binnenzijde", 
        desc: "Handmatig en grondig, op afspraak."
      },
      {
        title: "Dakramen en velux",
        desc: "Moeilijk bereikbaar? Geen probleem. Wij reinigen ook lastig bereikbare ramen vanaf de grond."
      },
      {
        title: "Raamkaders",
        desc: "Worden standaard mee gereinigd."
      }
    ],
    benefits: [
      {
        icon: Droplets,
        title: "Osmosewater",
        desc: "Droogt vlekkeloos op, geen strepen."
      },
      {
        icon: Shield,
        title: "Tot grote hoogte",
        desc: "We bereiken ook hoge ramen zonder ladder."
      },
      {
        icon: CheckCircle2,
        title: "Flexibel",
        desc: "Planning op afspraak, ook 's avonds of in het weekend."
      },
      {
        icon: Sparkles,
        title: "Streeploos resultaat",
        desc: "Glanzend helder, ook bij fel zonlicht."
      }
    ],
    features: [
      {
        title: "Kaders inbegrepen",
        desc: "We reinigen altijd de raamkaders mee."
      },
      {
        title: "Niet thuis? Geen probleem",
        desc: "U hoeft niet aanwezig te zijn voor de buitenkant."
      },
      {
        title: "Geen contracten",
        desc: "Eenmalig of vaste frequentie, u kiest zelf."
      },
      {
        title: "Vaste prijs vooraf",
        desc: "Wat we afspreken is wat u betaalt, geen verrassingen achteraf."
      }
    ],
    stats: [
      { number: "100+", label: "Woningen" },
      { number: "54+", label: "Bedrijven" },
      { number: "150+", label: "Tevreden klanten" }
    ],
    beforeAfterImages: [],
    useSlider: false,
    faqs: [
      {
        question: "Wat is osmosewater?",
        answer: "Gefilterd water zonder kalk. Het droogt vlekkeloos op en laat geen strepen of kalkaanslag achter op uw ramen."
      },
      {
        question: "Hoe vaak laat ik mijn ramen best wassen?",
        answer: "Voor woningen raden we aan om de drie maanden te reinigen. Voor winkels en handelszaken werken we vaak iedere vier tot zes weken voor een blijvend professionele uitstraling."
      },
      {
        question: "Moet ik thuis zijn?",
        answer: "Niet voor de buitenkant. Voor binnenramen spreken we vooraf een moment af dat u past."
      },
      {
        question: "Werken jullie met contracten?",
        answer: "Nee. U kiest zelf of u eenmalig wil reinigen of op een vaste frequentie wenst te werken."
      },
      {
        question: "Reinigen jullie ook moeilijk bereikbare ramen?",
        answer: "Ja. Met telescopische steel reinigen we tot grote hoogte vanaf de grond. Dakramen, velux en andere lastige plekken pakken we mee aan."
      }
    ]
  },
  "dakreiniging": {
    id: "dakreiniging",
    title: "Dakreiniging",
    subtitle: "Dakreiniging: mos, algen en zwarte aanslag grondig verwijderd. Zonder schade aan uw dak.",
    description: "Daken worden vuil door mos, korstmos, algen en luchtvervuiling. Dat is niet alleen lelijk, het tast op termijn ook uw dak aan. Voegen brokkelen af, dakpannen worden poreus. Het risico op waterinfiltratie en vorstschade neemt toe.",
    icon: RoofIcon,
    heroImage: "/images/dak.webp",
    startingPrice: "€350",
    services: [
      {
        title: "Softwash reiniging",
        desc: "We reinigen onder lage druk met milieuvriendelijke producten. Geen hogedrukreiniger die voegen uitspuit of coating beschadigt."
      },
      {
        title: "Hydrofuge behandeling",
        desc: "Optionele waterafstotende laag na de reiniging. Pannen mogen nat worden, maar niet nat blijven. Houdt uw dak langer in topconditie."
      },
      {
        title: "Mos en algenbehandeling",
        desc: "Hardnekkig mos, korstmos en algen worden volledig verwijderd met een aangepaste behandeling op maat van uw daktype."
      },
      {
        title: "Dakgoten meereinigen",
        desc: "Verstopte goten ruimen we mee uit. Voorkomt waterinfiltratie en schade aan gevel of fundering."
      }
    ],
    benefits: [
      {
        icon: Shield,
        title: "Langere levensduur",
        desc: "Regelmatig reinigen verlengt de levensduur van uw dak en voorkomt dure herstellingen."
      },
      {
        icon: Zap,
        title: "Hogere woningwaarde",
        desc: "Een schoon dak verhoogt de uitstraling en behoudt de waarde van uw woning."
      },
      {
        icon: Droplets,
        title: "Geen waterinfiltratie",
        desc: "Verminder het risico op waterinfiltratie en vorstschade door schone dakpannen en goten."
      },
      {
        icon: Sparkles,
        title: "Persoonlijke aanpak",
        desc: "De zaakvoerder is zelf actief op de werf en kijkt mee voor een vlekkeloos resultaat."
      }
    ],
    features: [
      {
        title: "Stoomreiniging tot 100°C",
        desc: "Bij hardnekkige mosvorming reinigen we met hete stoom. Algen en mos worden afgedood vanaf 100°C, terwijl de dakpannen volledig intact blijven."
      },
      {
        title: "Milieuvriendelijke producten",
        desc: "We gebruiken biologisch afbreekbare reinigingsmiddelen die effectief zijn maar geen schade aan het milieu veroorzaken."
      },
      {
        title: "Gratis inspectie",
        desc: "We melden eventuele schade altijd. Voor elke dakreiniging voeren we een grondige inspectie uit en rapporteren gebreken."
      },
      {
        title: "Tevredenheidsgarantie",
        desc: "Niet tevreden? Dan komen we kosteloos terug tot het resultaat is wat u verwacht."
      }
    ],
    stats: [
      { number: "200+", label: "Daken gereinigd" },
      { number: "5+", label: "Jaar ervaring" },
      { number: "100%", label: "Klanttevredenheid" }
    ],
    faqs: [
      {
        question: "Is de reiniging schadelijk voor mijn dak?",
        answer: "Nee. Wij werken altijd met softwash onder lage druk en milieuvriendelijke producten die specifiek afgestemd zijn op uw daktype. Een hogedrukreiniger kan wel schade aanrichten. Daarom gebruiken we die bewust niet voor daken."
      },
      {
        question: "Waarom zou ik mijn dak laten reinigen?",
        answer: "Een schoon dak gaat niet alleen om uitstraling. Regelmatig reinigen verlengt de levensduur van uw dakpannen, voorkomt dure herstellingen en vermindert het risico op waterinfiltratie en vorstschade. Bovendien behoudt uw woning haar waarde."
      },
      {
        question: "Wat is een hydrofuge behandeling en heb ik die nodig?",
        answer: "Hydrofuge is een waterafstotende laag die we na de reiniging aanbrengen. Dakpannen mogen nat worden maar moeten niet nat blijven. Dankzij hydrofuge droogt uw dak sneller op, krijgt mos minder kans en blijft alles langer in topconditie."
      },
      {
        question: "Kan mijn dak gereinigd worden als er zonnepanelen op liggen?",
        answer: "Ja, dat is zeker mogelijk. We raden zelfs aan om het dak eerst te laten reinigen vóór u zonnepanelen laat installeren. Heeft u al panelen liggen, dan reinigen we zorgvuldig rondom zonder enige schade."
      },
      {
        question: "Wat als jullie schade vaststellen aan mijn dak?",
        answer: "We melden dit altijd. Bij elke opdracht voeren we vooraf een grondige inspectie uit en rapporteren we eventuele gebreken. Zo kunt u tijdig herstellingen laten uitvoeren."
      },
      {
        question: "Wat als ik niet tevreden ben met het resultaat?",
        answer: "Dan komen we kosteloos terug tot het resultaat is wat u verwacht. Wij werken pas af als u 100% tevreden bent."
      }
    ]
  },
  "zonnepaneel-schoonmaak": {
    id: "zonnepaneel-schoonmaak",
    title: "Zonnepaneel schoonmaak",
    subtitle: "Zonnepanelen reinigen met osmosewater. Tot 30% meer rendement met professionele reiniging.",
    description: "Vuile zonnepanelen kunnen tot 30% minder energie opwekken. Wij zorgen voor maximaal rendement.",
    icon: SolarIcon,
    heroImage: "/images/zonnepanelen.webp",
    heroObjectPosition: "center center",
    startingPrice: "€8/paneel",
    services: [
      {
        title: "Complete paneel reiniging",
        desc: "Grondige reiniging van al uw zonnepanelen met osmose water voor een streeploos resultaat."
      },
      {
        title: "Rendement check",
        desc: "Na reiniging controleren we of uw panelen optimaal presteren en geven we advies voor onderhoud."
      },
      {
        title: "Frame en rand reiniging",
        desc: "Naast het glas reinigen we ook de aluminium frames en randen waar vuil en mos zich opstapelt."
      },
      {
        title: "Volledig vanaf de grond",
        desc: "Met telescopische steel reinigen we tot 20 meter hoog. Geen risico op schade aan dak of panelen."
      }
    ],
    benefits: [
      {
        icon: Zap,
        title: "Tot 30% meer opbrengst",
        desc: "Schone panelen produceren significant meer energie. Verdien uw investering snel terug."
      },
      {
        icon: Shield,
        title: "Langere levensduur",
        desc: "Regelmatige reiniging voorkomt permanente vlekken en verlengt de levensduur van uw panelen."
      },
      {
        icon: Droplets,
        title: "Osmose water",
        desc: "We gebruiken puur osmose water dat geen kalkaanslag of strepen achterlaat."
      },
      {
        icon: CheckCircle2,
        title: "Veilig voor coating",
        desc: "Onze methode tast de anti-reflectie coating van uw panelen niet aan, in tegenstelling tot regen alleen."
      }
    ],
    features: [
      {
        title: "Zachte reinigingstechniek",
        desc: "Onze methode is veilig voor alle soorten zonnepanelen en beschadigt het oppervlak niet."
      },
      {
        title: "Geen chemicaliën",
        desc: "Puur water reiniging zonder agressieve chemicaliën die de coating kunnen beschadigen."
      },
      {
        title: "Flexibele planning",
        desc: "We komen op een moment dat het u uitkomt, ook in het weekend indien gewenst."
      },
      {
        title: "Jaarlijks onderhoud",
        desc: "1 à 2 keer per jaar reinigen geeft het beste rendement. We sturen u optioneel een herinnering."
      }
    ],
    stats: [
      { number: "500+", label: "Panelen gereinigd" },
      { number: "30%", label: "Meer rendement" },
      { number: "2x", label: "Per jaar aanbevolen" }
    ]
  },
  "gevel-reiniging": {
    id: "gevel-reiniging",
    title: "Oprit en gevelreiniging",
    subtitle: "Gevel- en opritreiniging. Opritten, terrassen en gevels weer als nieuw.",
    description: "Vuil, mos, algen en zwarte aanslag maken uw oprit glad en onveilig, en tasten uw gevel aan. We reinigen baksteen, crepi, klinkers, beton en natuursteen grondig zonder schade aan het oppervlak.",
    icon: PaversIcon,
    heroImage: "/images/oprit.webp",
    heroObjectPosition: "70% 30%",
    startingPrice: "€250",
    beforeAfterPair: {
      before: "/images/gevel-voor.webp",
      after: "/images/gevel-na.webp",
      label: "Ramen en gevel na professionele reiniging"
    },
    services: [
      {
        title: "Oprit en terrasreiniging",
        desc: "Klinkers, keramiek, beton en natuursteen grondig gereinigd. Vlekken, mos en aanslag verdwijnen zonder dat uw tegels poreus worden."
      },
      {
        title: "Gevelreiniging op maat",
        desc: "Baksteen, crepi, hout en composiet. Per geveltype kiezen we de juiste techniek met softwash, hete stoom of gerichte hoge druk."
      },
      {
        title: "Hydrofuge behandeling",
        desc: "Een waterafstotend regenjasje voor uw gevel. Vocht, mos en algen krijgen jarenlang geen kans en de uitstraling blijft fris."
      },
      {
        title: "Oleofuge behandeling",
        desc: "Voor opritten en terrassen. Ook olie en vet kunnen geen vlekken meer maken. Ideaal naast de auto of in de bbq zone."
      }
    ],
    benefits: [
      {
        icon: Zap,
        title: "Direct zichtbaar resultaat",
        desc: "Spectaculaire voor-en-na resultaten. Uw oprit of gevel ziet er na enkele uren al jaren jonger uit."
      },
      {
        icon: Shield,
        title: "Voorkomt vochtproblemen",
        desc: "Een vervuilde gevel houdt vocht vast. Tijdig reinigen voorkomt schade aan metselwerk en binnenmuren."
      },
      {
        icon: Star,
        title: "Hogere woningwaarde",
        desc: "Een verzorgde oprit en gevel verhogen de uitstraling en waarde van uw woning aanzienlijk."
      },
      {
        icon: Droplets,
        title: "Veilig oppervlak",
        desc: "Gladde mos en algen weg. Geen risico meer op uitglijden bij nat weer of na regen."
      }
    ],
    features: [
      {
        title: "Stoomreiniging tot 100°C",
        desc: "Bij hardnekkige vervuiling werken we met hete stoom: mos en algen worden afgedood, het oppervlak blijft volledig intact. Geen agressieve hoge druk nodig."
      },
      {
        title: "Maatwerk per materiaal",
        desc: "Klinkers, beton, natuursteen, baksteen, crepi, hout - elk materiaal vraagt een eigen aanpak. Wij stemmen techniek én druk altijd af op uw situatie."
      },
      {
        title: "Nette werkplek",
        desc: "Na de klus ruimen we alles netjes op. Geen vuil, geen rommel, geen gedoe. We laten uw eigendom achter zoals we ze gevonden hebben."
      },
      {
        title: "Tevredenheidsgarantie",
        desc: "U betaalt pas wanneer u 100% tevreden bent. Niet goed? Dan komen we kosteloos terug."
      }
    ],
    stats: [
      { number: "150+", label: "Opritten gereinigd" },
      { number: "1000m²", label: "Gemiddeld per week" },
      { number: "100%", label: "Tevreden klanten" }
    ],
    faqs: [
      {
        question: "Welke ondergronden reinigen jullie?",
        answer: "Wij reinigen klinkers, keramische tegels, betonklinkers, natuursteen, baksteen, crepi, hout en composiet. Per materiaal kiezen we de juiste techniek zoals softwash, hete stoom of gerichte hoge druk."
      },
      {
        question: "Is hoge druk reiniging schadelijk voor mijn oprit of gevel?",
        answer: "Niet als het correct wordt uitgevoerd. Wij stemmen de drukkracht altijd af op het materiaal. Voor gevels werken we vaak met softwash om voegen en coating te beschermen. Voor opritten zetten we waar nodig hete stoom in zodat het oppervlak intact blijft."
      },
      {
        question: "Wat doet stoomreiniging precies?",
        answer: "Bij stoomreiniging gebruiken we water dat verhit wordt tot ongeveer 100 graden. Mos en algen worden afgedood op die temperatuur terwijl uw tegels of klinkers volledig intact blijven. Geen agressieve hoge druk, wel een grondig resultaat."
      },
      {
        question: "Hoe lang blijft mijn oprit of gevel schoon na reiniging?",
        answer: "Dit hangt af van de locatie en omstandigheden. Gemiddeld blijft uw oprit twee tot drie jaar mooi schoon. Met een hydrofuge of oleofuge behandeling na de reiniging verlengt u dit tot vier of vijf jaar."
      },
      {
        question: "Wat is het verschil tussen hydrofuge en oleofuge?",
        answer: "Hydrofuge maakt uw gevel of oprit waterafstotend zodat regen en vocht geen kans krijgen. Oleofuge gaat een stap verder en stoot ook olie en vet af. Ideaal naast de auto of bbq zone."
      },
      {
        question: "Kunnen jullie ook onkruid tussen de voegen verwijderen?",
        answer: "Ja, tijdens de reiniging halen we onkruid uit de voegen. Voor langdurig resultaat raden we aan om de voegen achteraf opnieuw te vullen of te impregneren."
      },
      {
        question: "Reinigen jullie ook terrassen en tuinpaden?",
        answer: "Absoluut. Naast opritten en gevels reinigen we ook terrassen, tuinpaden, poolranden en alle andere verharde oppervlakken rondom uw woning."
      },
      {
        question: "Wat als ik niet tevreden ben met het resultaat?",
        answer: "Dan komen we kosteloos terug tot het resultaat is wat u verwacht. U betaalt pas wanneer u 100% tevreden bent."
      }
    ]
  }
};

// Quote Form Component
const QuoteForm = ({ serviceName, serviceId }) => {
  const [formData, setFormData] = useState({
    naam: "",
    email: "",
    telefoon: "",
    adres: "",
    dienst: serviceId,
    bericht: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.naam || !formData.email || !formData.telefoon || !formData.adres) {
      toast.error("Vul alle verplichte velden in");
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Send to Formspree for email notification
      await axios.post("https://formspree.io/f/xdalonng", {
        naam: formData.naam,
        email: formData.email,
        telefoon: formData.telefoon,
        adres: formData.adres,
        dienst: serviceId,
        bericht: formData.bericht || "Geen bericht"
      });
      
      // Push to dataLayer for GTM tracking
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'form_submission_success',
        form_name: 'service_quote_form',
        form_dienst: serviceId
      });
      
      toast.success("Bedankt! Uw aanvraag is verzonden. We nemen snel contact met u op.");
      setFormData({
        naam: "",
        email: "",
        telefoon: "",
        adres: "",
        dienst: serviceId,
        bericht: ""
      });
    } catch (error) {
      console.error("Error submitting quote:", error);
      toast.error("Er ging iets mis. Probeer het opnieuw of bel ons.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-12 sm:py-20 bg-white" id="offerte">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 sm:mb-12 max-w-2xl">
          <h2 
            className="text-2xl sm:text-4xl font-bold text-slate-900 mb-3 sm:mb-4"
            style={{ fontFamily: 'Instrument Sans, sans-serif' }}
          >
            Vraag nu een offerte aan.
          </h2>
          <p className="text-slate-600 text-sm sm:text-lg">
            Vul het formulier in en ontvang binnen 24 uur een vrijblijvende offerte voor {serviceName.toLowerCase()}.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-slate-50 rounded-3xl p-8 lg:p-10 space-y-6 max-w-4xl mx-auto" data-testid="service-quote-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="naam" className="text-slate-700 font-medium mb-2 block">Naam *</Label>
              <Input
                id="naam"
                name="naam"
                value={formData.naam}
                onChange={handleChange}
                placeholder="Uw naam"
                className="h-12 rounded-xl border-slate-200 bg-white"
                data-testid="service-input-naam"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-slate-700 font-medium mb-2 block">E-mail *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="uw@email.be"
                className="h-12 rounded-xl border-slate-200 bg-white"
                data-testid="service-input-email"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="telefoon" className="text-slate-700 font-medium mb-2 block">Telefoon *</Label>
              <Input
                id="telefoon"
                name="telefoon"
                value={formData.telefoon}
                onChange={handleChange}
                placeholder="Uw telefoonnummer"
                className="h-12 rounded-xl border-slate-200 bg-white"
                data-testid="service-input-telefoon"
              />
            </div>
            <div>
              <Label htmlFor="adres" className="text-slate-700 font-medium mb-2 block">Adres *</Label>
              <Input
                id="adres"
                name="adres"
                value={formData.adres}
                onChange={handleChange}
                placeholder="Straat, nummer, gemeente"
                className="h-12 rounded-xl border-slate-200 bg-white"
                data-testid="service-input-adres"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="bericht" className="text-slate-700 font-medium mb-2 block">Extra informatie (optioneel)</Label>
            <Textarea
              id="bericht"
              name="bericht"
              value={formData.bericht}
              onChange={handleChange}
              placeholder="Vertel ons meer over uw project..."
              className="rounded-xl border-slate-200 bg-white min-h-[120px]"
              data-testid="service-input-bericht"
            />
          </div>

          <Button 
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-full py-6 text-lg bg-sky-500 hover:bg-sky-600 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            data-testid="service-submit-btn"
          >
            {isSubmitting ? "Verzenden..." : (
              <>
                Offerte aanvragen
                <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
        </form>
      </div>
    </section>
  );
};

// Main Service Page Component
export default function ServicePage() {
  const { serviceId } = useParams();
  const service = serviceData[serviceId];

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [serviceId]);

  // SEO: update meta tags + inject service schema per page
  useEffect(() => {
    const seo = SEO_CONFIG[serviceId];
    if (!seo || !service) return;
    updateMetaTags({
      title: seo.title,
      description: seo.description,
      keywords: seo.keywords,
      canonicalPath: `/diensten/${serviceId}`,
    });
    injectServiceSchema({ ...seo, description: service.description }, serviceId);

    return () => {
      // restore home defaults when leaving service pages
      updateMetaTags({
        title: "Fresh View | Professionele Ramen Wassen & Reiniging Leuven",
        description: "Fresh View - Professionele schoonmaakdiensten in Leuven en omstreken. Ramen wassen, dakreiniging, zonnepaneel schoonmaak en oprit reiniging. Gratis offerte binnen 24 uur!",
        keywords: "ramen wassen Leuven, glazenwasser Leuven, dakreiniging Leuven, zonnepanelen reinigen Leuven, gevelreiniging Leuven, opritreiniging Leuven, Fresh View",
        canonicalPath: "",
      });
      const old = document.getElementById("service-schema-jsonld");
      if (old) old.remove();
    };
  }, [serviceId, service]);

  // Helper function to scroll to the quote form
  const scrollToQuote = () => {
    document.getElementById('offerte')?.scrollIntoView({ behavior: 'smooth' });
  };

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Dienst niet gevonden</h1>
          <Link to="/" className="text-sky-600 hover:text-sky-700">
            Terug naar home
          </Link>
        </div>
      </div>
    );
  }

  const ServiceIcon = service.icon;

  return (
    <div className="min-h-screen bg-slate-50" data-testid={`service-page-${serviceId}`}>
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="service-logo">
              <img 
                src="/images/logo.webp" 
                alt="Fresh View Logo" 
                className="h-12 w-auto"
              />
            </Link>
            <div className="flex items-center gap-4">
              <a 
                href="tel:0494614295" 
                className="hidden sm:flex items-center gap-2 text-slate-700 hover:text-sky-600 font-medium transition-colors duration-300"
              >
                <Phone className="w-4 h-4" />
                <span>0494 61 42 95</span>
              </a>
              <Link to="/">
                <Button variant="outline" className="rounded-full" data-testid="back-to-home-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Terug
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section - Full-width image with title overlay, CTAs below */}
      <section className="pt-16 sm:pt-20 bg-white">
        {/* Full-width background image/video with overlay title */}
        <div className="relative w-full aspect-[16/11] sm:aspect-[16/9] lg:aspect-[21/9] max-h-[560px] overflow-hidden">
          {service.heroVideo ? (
            <video
              src={service.heroVideo}
              poster={service.heroImage}
              autoPlay
              muted
              loop
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: service.heroObjectPosition || 'center 35%' }}
            >
              <source src={service.heroVideo} type="video/mp4" />
              <img src={service.heroImage} alt={service.title} className="w-full h-full object-cover" />
            </video>
          ) : (
            <img
              src={service.heroImage.replace('.webp','-1200.webp')}
              srcSet={`${service.heroImage.replace('.webp','-480.webp')} 480w, ${service.heroImage.replace('.webp','-800.webp')} 800w, ${service.heroImage.replace('.webp','-1200.webp')} 1200w, ${service.heroImage.replace('.webp','-1600.webp')} 1600w`}
              sizes="100vw"
              alt={service.title}
              width="1200"
              height="514"
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className={`absolute inset-0 w-full h-full object-cover ${service.heroObjectPositionClass || ''}`}
              style={service.heroObjectPositionClass ? undefined : { objectPosition: service.heroObjectPosition || 'center 35%' }}
            />
          )}

          {/* Gradient overlay + title & description on photo */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
          <div className="absolute inset-0 flex items-end">
            <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6 sm:pb-10">
              <div className="max-w-3xl">
                <h1
                  className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-2 sm:mb-4 drop-shadow"
                  style={{ fontFamily: 'Instrument Sans, sans-serif' }}
                >
                  {service.subtitle}
                </h1>
                <p className="text-sm sm:text-base lg:text-lg text-white/90 leading-relaxed max-w-2xl">
                  {service.description}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTAs below image */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
          <div className="max-w-3xl">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button
                onClick={scrollToQuote}
                className="rounded-full px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg bg-sky-500 hover:bg-sky-600 text-white font-semibold shadow-lg"
                data-testid="service-hero-cta"
              >
                Gratis offerte aanvragen
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <a
                href="tel:0494614295"
                className="inline-flex items-center justify-center gap-2 rounded-full px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg font-medium border-2 border-slate-200 text-slate-700 hover:border-sky-500 hover:text-sky-600 transition-all duration-300"
              >
                <Phone className="w-5 h-5" />
                Bel nu
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 sm:mb-16 max-w-2xl">
            <h2 
              className="text-2xl sm:text-4xl font-bold text-slate-900 mb-3 sm:mb-4"
              style={{ fontFamily: 'Instrument Sans, sans-serif' }}
            >
              Onze diensten.
            </h2>
          </div>
          <div className={`grid grid-cols-2 ${service.services.length === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2'} gap-3 sm:gap-8`}>
            {service.services.map((s, index) => (
              <div 
                key={index}
                className="p-4 sm:p-8 rounded-2xl bg-slate-50 hover:bg-sky-50 border border-slate-100 hover:border-sky-100 transition-all duration-300"
              >
                <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-xl bg-sky-100 flex items-center justify-center text-sky-600 mb-3 sm:mb-4">
                  <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <h3 className="text-base sm:text-xl font-semibold text-slate-900 mb-2 sm:mb-3 leading-tight" style={{ fontFamily: 'Instrument Sans, sans-serif' }}>
                  {s.title}
                </h3>
                <p className="text-slate-600 text-sm sm:text-base leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>

          {/* Full-width visual below services grid */}
          <div className="mt-10 sm:mt-16 rounded-2xl overflow-hidden shadow-md">
            <img
              src="/images/service-section-1200.webp"
              srcSet="/images/service-section-480.webp 480w, /images/service-section-800.webp 800w, /images/service-section-1200.webp 1200w, /images/service-section-1600.webp 1600w"
              sizes="(min-width: 1024px) 1152px, 100vw"
              alt="Fresh View professionele reiniging aan het werk"
              width="1600"
              height="900"
              loading="lazy"
              decoding="async"
              className="w-full h-auto block"
              data-testid="service-section-image"
            />
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 sm:py-20 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 sm:mb-16 max-w-2xl">
            <h2 
              className="text-2xl sm:text-4xl font-bold text-slate-900 mb-3 sm:mb-4"
              style={{ fontFamily: 'Instrument Sans, sans-serif' }}
            >
              Hoe wij het verschil maken.
            </h2>
          </div>
          <div className={`grid grid-cols-2 ${service.benefits.length === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3'} gap-3 sm:gap-8`}>
            {service.benefits.map((benefit, index) => {
              const BenefitIcon = benefit.icon;
              return (
                <div 
                  key={index}
                  className="p-4 sm:p-8 rounded-2xl bg-white shadow-sm hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-11 h-11 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br from-sky-100 to-emerald-100 flex items-center justify-center text-sky-600 mb-3 sm:mb-6">
                    <BenefitIcon className="w-5 h-5 sm:w-8 sm:h-8" />
                  </div>
                  <h3 className="text-base sm:text-xl font-semibold text-slate-900 mb-2 sm:mb-3 leading-tight" style={{ fontFamily: 'Instrument Sans, sans-serif' }}>
                    {benefit.title}
                  </h3>
                  <p className="text-slate-600 text-sm sm:text-base leading-relaxed">{benefit.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 sm:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 sm:mb-16 max-w-2xl">
            <h2 
              className="text-2xl sm:text-4xl font-bold text-slate-900 mb-3 sm:mb-4"
              style={{ fontFamily: 'Instrument Sans, sans-serif' }}
            >
              Wat ons onderscheidt.
            </h2>
          </div>
          <div className={`grid grid-cols-2 ${service.features.length === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3'} gap-3 sm:gap-8`}>
            {service.features.map((feature, index) => (
              <div 
                key={index}
                className="p-4 sm:p-8 rounded-2xl bg-white border border-slate-100 hover:border-sky-100 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-sky-500 text-white flex items-center justify-center font-bold text-sm sm:text-lg mb-3 sm:mb-4">
                  {index + 1}
                </div>
                <h3 className="text-base sm:text-xl font-semibold text-slate-900 mb-2 sm:mb-3 leading-tight" style={{ fontFamily: 'Instrument Sans, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-slate-600 text-sm sm:text-base leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section - removed */}

      {/* Before/After Draggable Slider - Single pair (e.g. gevel-reiniging) */}
      {service.beforeAfterPair && (
        <section className="py-14 sm:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-6 sm:mb-10 max-w-2xl">
              <span className="text-sky-600 font-semibold text-sm tracking-wide">Resultaten</span>
              <h2
                className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3 mb-3"
                style={{ fontFamily: 'Instrument Sans, sans-serif' }}
              >
                Voor & na
              </h2>
              <p className="text-slate-600 text-base sm:text-lg">
                Sleep de knop om het verschil zelf te zien
              </p>
            </div>
            <div className="max-w-xs sm:max-w-lg md:max-w-xl mx-auto">
              <BeforeAfterSlider
                before={service.beforeAfterPair.before}
                after={service.beforeAfterPair.after}
                aspect="aspect-[3/4]"
              />
            </div>
            <div className="mt-8 sm:mt-10 flex justify-center">
              <Button
                onClick={scrollToQuote}
                className="rounded-full px-8 py-6 text-lg bg-sky-500 hover:bg-sky-600 text-white font-semibold shadow-lg"
                data-testid="cta-quote-after-slider"
              >
                Vraag nu uw gratis offerte aan
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Before/After Section with Slider - Only for ramen-wassen */}
      {service.beforeAfterImages && service.beforeAfterImages.length > 0 && service.useSlider && (
        <section className="py-14 sm:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 sm:mb-12">
              <span className="text-sky-600 font-semibold text-sm tracking-wide">Resultaten</span>
              <h2 
                className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3 mb-4"
                style={{ fontFamily: 'Instrument Sans, sans-serif' }}
              >
                Voor & na
              </h2>
              <p className="text-slate-600 text-lg max-w-2xl mx-auto">
                Bekijk het verschil dat onze professionele reiniging maakt
              </p>
            </div>
            <ImageSlider images={service.beforeAfterImages} />
            {/* CTA after before/after */}
            <div className="text-center mt-12">
              <p className="text-slate-600 mb-4">Wilt u ook zulke resultaten?</p>
              <Button 
                onClick={scrollToQuote}
                className="rounded-full px-8 py-6 text-lg bg-sky-500 hover:bg-sky-600 text-white font-semibold shadow-lg"
              >
                Vraag nu uw gratis offerte aan
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Before/After Section without Slider */}
      {service.beforeAfterImages && service.beforeAfterImages.length > 0 && !service.useSlider && (
        <section className="py-14 sm:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 sm:mb-12">
              <span className="text-sky-600 font-semibold text-sm tracking-wide">Resultaten</span>
              <h2 
                className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3 mb-4"
                style={{ fontFamily: 'Instrument Sans, sans-serif' }}
              >
                Voor & na
              </h2>
              <p className="text-slate-600 text-lg max-w-2xl mx-auto">
                Bekijk het verschil dat onze professionele reiniging maakt
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {service.beforeAfterImages.map((image, index) => (
                <div key={index} className="rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <img 
                    src={image} 
                    alt={`Voor en na reiniging ${index + 1}`} 
                    className="w-full h-64 object-cover"
                  />
                </div>
              ))}
            </div>
            {/* CTA after before/after */}
            <div className="text-center mt-12">
              <p className="text-slate-600 mb-4">Wilt u ook zulke resultaten?</p>
              <Button 
                onClick={scrollToQuote}
                className="rounded-full px-8 py-6 text-lg bg-sky-500 hover:bg-sky-600 text-white font-semibold shadow-lg"
              >
                Vraag nu uw gratis offerte aan
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Result Images Section - For other services */}
      {service.resultImages && service.resultImages.length > 0 && (
        <section className="py-12 sm:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 sm:mb-12">
              <span className="text-sky-600 font-semibold text-xs sm:text-sm tracking-wide">Ons werk</span>
              <h2 
                className="text-2xl sm:text-4xl font-bold text-slate-900 mt-2 sm:mt-3 mb-3 sm:mb-4"
                style={{ fontFamily: 'Instrument Sans, sans-serif' }}
              >
              Resultaten die spreken
              </h2>
              <p className="text-slate-600 text-sm sm:text-lg max-w-2xl mx-auto">
                Bekijk voorbeelden van ons professionele werk
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-6 max-w-5xl mx-auto">
              {service.resultImages.map((image, index) => (
                <div key={index} className="rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 group">
                  <img 
                    src={image} 
                    alt={`${service.title} resultaat ${index + 1}`} 
                    className="w-full h-40 sm:h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
              ))}
            </div>
            {/* CTA after results */}
            <div className="text-center mt-8 sm:mt-12">
              <p className="text-slate-600 mb-4 text-sm sm:text-base">Wilt u ook zulke resultaten?</p>
              <Button 
                onClick={scrollToQuote}
                className="rounded-full px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg bg-sky-500 hover:bg-sky-600 text-white font-semibold shadow-lg"
              >
                Vraag nu uw gratis offerte aan
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Price Calculator - Temporarily hidden */}
      {false && serviceId === "ramen-wassen" && <PriceCalculator />}

      {/* Quote Form - shown on all pages while calculator is hidden */}
      <QuoteForm serviceName={service.title} serviceId={service.id} />

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* FAQ Section - At the bottom of all pages */}
      {service.faqs && service.faqs.length > 0 && (
        <section className="py-12 sm:py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-6 sm:mb-12 max-w-2xl">
              <span className="text-sky-600 font-semibold text-xs sm:text-sm tracking-wide">Veelgestelde vragen</span>
              <h2 
                className="text-2xl sm:text-4xl font-bold text-slate-900 mt-2 sm:mt-3 mb-3 sm:mb-4"
                style={{ fontFamily: 'Instrument Sans, sans-serif' }}
              >
              Deze vragen krijgen we vaak.
              </h2>
            </div>
            <div className="space-y-3 sm:space-y-4">
              {service.faqs.map((faq, index) => (
                <FAQItem key={index} question={faq.question} answer={faq.answer} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Werkgebied / Local SEO Section */}
      <section className="py-10 sm:py-14 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="text-lg sm:text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Instrument Sans, sans-serif' }}>
              {service.title} in heel Vlaams-Brabant.
            </h2>
            <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
              Fresh View is uw lokale partner voor {service.title.toLowerCase()} in Vlaams-Brabant. Wij bedienen{" "}
              <strong>Leuven</strong>, <strong>Aarschot</strong>, <strong>Tienen</strong>, <strong>Diest</strong>, Heverlee,
              Holsbeek, Rotselaar, Lubbeek, Tremelo en Begijnendijk. Geen verplaatsingskosten binnen de regio.
            </p>
          </div>
        </div>
      </section>

      <PreFooterBanner />

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-10 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="/images/logo.webp" 
                alt="Fresh View Logo" 
                className="h-10 w-auto brightness-0 invert"
              />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
              <a href="tel:0494614295" className="flex items-center gap-2 text-slate-400 hover:text-sky-400 transition-colors">
                <Phone className="w-4 h-4" />
                0494 61 42 95
              </a>
            </div>
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} Fresh View. Alle rechten voorbehouden.
            </p>
          </div>
        </div>
      </footer>
      <MobileBottomNav />
    </div>
  );
}
